import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  useTheme,
  Switch,
  FormControlLabel,
  Alert,
  Button,
  ListSubheader,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  TrendingUp,
  ShowChart,
  Psychology,
  AutoGraph,
  Assessment,
  Settings as SettingsIcon,
  Timeline,
  AccountBalance,
  Warning as WarningIcon,
  BarChart,
  Lightbulb,
  BugReport,
  Schedule,
  Logout as LogoutIcon,
  Campaign as CampaignIcon,
  Newspaper as NewspaperIcon,
  RocketLaunch as RocketLaunchIcon,
  Radar as RadarIcon,
  FeedOutlined as FeedIcon,
  AdminPanelSettings as AdminIcon,
  Insights as InsightsIcon,
  CrisisAlert as CrisisAlertIcon,
  MenuBook as MenuBookIcon,
} from '@mui/icons-material';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import ScienceIcon from '@mui/icons-material/Science';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import { useAccount } from '../contexts/AccountContext';
import EvidenceBadge from './EvidenceBadge';
import { apiService } from '../services/api';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { logout } from '../store/slices/authSlice';

const drawerWidth = 240;

interface LayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  badge?: string;
  group?: string;
}

// Grouped by what the page is for; "Testing" holds the pages that measure the others.
const navItems: NavItem[] = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/', group: 'Your money' },
  { text: 'Portfolio Risk', icon: <CrisisAlertIcon />, path: '/portfolio-risk', badge: 'NEW', group: 'Your money' },
  { text: 'Trade Journal', icon: <MenuBookIcon />, path: '/trade-journal', group: 'Your money' },
  { text: 'Call Tracker', icon: <CampaignIcon />, path: '/calls', badge: 'NEW', group: 'Your money' },
  { text: 'Trading', icon: <AccountBalance />, path: '/trading', group: 'Your money' },
  { text: 'Top Picks', icon: <InsightsIcon />, path: '/top-picks', badge: 'HOT', group: 'Picks' },
  { text: 'News Movers', icon: <NewspaperIcon />, path: '/news-movers', group: 'Picks' },
  { text: 'News Signals', icon: <CampaignIcon />, path: '/news-signals', group: 'Picks' },
  { text: 'Stocks of Future', icon: <RocketLaunchIcon />, path: '/future-stocks', group: 'Picks' },
  { text: 'Future Stocks Lab', icon: <ScienceIcon />, path: '/future-stocks-lab', badge: 'EXP', group: 'Picks' },
  { text: 'Media Trending', icon: <NewspaperIcon />, path: '/media-trending', group: 'Picks' },
  { text: 'Catalyst Chains', icon: <ScienceIcon />, path: '/catalyst-chains', badge: 'SHADOW', group: 'Picks' },
  { text: 'Top Movers', icon: <AutoGraph />, path: '/movers', group: 'Picks' },
  { text: 'Recommendations', icon: <Lightbulb />, path: '/recommendations', group: 'Picks' },
  { text: 'Options', icon: <InsightsIcon />, path: '/options', badge: 'BETA', group: 'Picks' },
  { text: 'Market Valuation', icon: <CrisisAlertIcon />, path: '/market-valuation', group: 'Market' },
  { text: 'Market Position', icon: <BarChart />, path: '/position', group: 'Market' },
  { text: 'Israeli Startups', icon: <RadarIcon />, path: '/israeli-startups', group: 'Market' },
  { text: 'Israeli News', icon: <FeedIcon />, path: '/israeli-news', group: 'Market' },
  { text: 'Stock Scoring', icon: <Assessment />, path: '/scoring', group: 'Look at a stock' },
  { text: 'Volatility Ranges', icon: <ShowChart />, path: '/ranges', group: 'Look at a stock' },
  { text: 'Earnings Moves', icon: <ShowChart />, path: '/earnings', badge: 'NEW', group: 'Look at a stock' },
  { text: 'ML Predictions', icon: <Psychology />, path: '/predictions', group: 'Look at a stock' },
  { text: 'Trading Signals', icon: <TrendingUp />, path: '/signals', group: 'Look at a stock' },
  { text: 'Pattern Matching', icon: <Timeline />, path: '/patterns', group: 'Look at a stock' },
  { text: 'Advanced Trading', icon: <AutoGraphIcon />, path: '/advanced', group: 'Look at a stock' },
  { text: 'Evidence', icon: <ScienceIcon />, path: '/evidence', badge: 'NEW', group: 'Testing' },
  { text: 'Signal Lab', icon: <ScienceIcon />, path: '/signal-lab', badge: 'NEW', group: 'Testing' },
  { text: 'Scoreboard', icon: <Assessment />, path: '/scoreboard', badge: 'NEW', group: 'Testing' },
  { text: 'Simulation', icon: <InsightsIcon />, path: '/simulation', group: 'Testing' },
  { text: 'Backtest', icon: <BugReport />, path: '/backtest', group: 'Testing' },
  { text: 'Daily Tasks', icon: <Schedule />, path: '/daily-tasks', group: 'Testing' },
  { text: 'System Health', icon: <MonitorHeartIcon />, path: '/health', group: 'Testing' },
  { text: 'Settings', icon: <SettingsIcon />, path: '/settings', group: 'Settings' },
];

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const username = useAppSelector((state) => state.auth.username);
  const isAdmin = useAppSelector((state) => state.auth.isAdmin);
  // Admin entry is visible only to admins, inserted just before Settings.
  const visibleNavItems: NavItem[] = isAdmin
    ? [...navItems.slice(0, -1), { text: 'Admin', icon: <AdminIcon />, path: '/admin', group: 'Settings' }, navItems[navItems.length - 1]]
    : navItems;
  const [mobileOpen, setMobileOpen] = useState(false);
  const { accountType, setAccountType, isLiveTrading } = useAccount();
  const [ibkrConnected, setIbkrConnected] = useState<boolean | null>(null);

  // Check IBKR connection status periodically
  useEffect(() => {
    const checkIBKRStatus = async () => {
      try {
        const status = await apiService.getIBKRStatus();
        setIbkrConnected(status.connected);
      } catch (error) {
        setIbkrConnected(false);
      }
    };

    // Check immediately
    checkIBKRStatus();

    // Then check every 10 seconds
    const interval = setInterval(checkIBKRStatus, 10000);

    return () => clearInterval(interval);
  }, []);

  // Log every page the user opens (best-effort) so admins can see navigation.
  const lastLoggedPath = React.useRef<string>('');
  useEffect(() => {
    if (location.pathname && location.pathname !== lastLoggedPath.current) {
      lastLoggedPath.current = location.pathname;
      apiService.logPageView(location.pathname);
    }
  }, [location.pathname]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleAccountToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newType = event.target.checked ? 'live' : 'paper';
    setAccountType(newType);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const drawer = (
    <Box>
      <Toolbar>
        <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 700 }}>
          Trading AI
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {visibleNavItems.map((item, i) => (
          <React.Fragment key={item.text}>
          {item.group && item.group !== visibleNavItems[i - 1]?.group && (
            <ListSubheader disableSticky sx={{ lineHeight: '28px', mt: i ? 1 : 0, bgcolor: 'transparent', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {item.group}
            </ListSubheader>
          )}
          <ListItem disablePadding>
            <ListItemButton
              component={RouterLink}
              to={item.path}
              selected={location.pathname === item.path}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.main + '20',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main + '30',
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: location.pathname === item.path ? theme.palette.primary.main : 'inherit',
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontWeight: location.pathname === item.path ? 600 : 400,
                }}
              />
              {item.badge && (
                <Chip
                  label={item.badge}
                  size="small"
                  color="warning"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
            </ListItemButton>
          </ListItem>
          </React.Fragment>
        ))}
      </List>
      <Divider />
      <Box sx={{ p: 2, mt: 'auto' }}>
        {isLiveTrading && (
          <Alert
            severity="warning"
            icon={<WarningIcon fontSize="small" />}
            sx={{ mb: 2, py: 0 }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              LIVE TRADING
            </Typography>
          </Alert>
        )}

        <FormControlLabel
          control={
            <Switch
              checked={isLiveTrading}
              onChange={handleAccountToggle}
              color={isLiveTrading ? 'warning' : 'primary'}
              size="small"
            />
          }
          label={
            <Typography variant="caption" sx={{ fontWeight: isLiveTrading ? 600 : 400 }}>
              {isLiveTrading ? 'Live Account' : 'Paper Account'}
            </Typography>
          }
          sx={{ mb: 1 }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: ibkrConnected === null
                ? theme.palette.grey[500]
                : ibkrConnected
                  ? theme.palette.success.main
                  : theme.palette.error.main,
              mr: 1,
            }}
          />
          <Typography
            variant="caption"
            color={ibkrConnected === false ? 'error' : 'text.secondary'}
            sx={{ fontWeight: ibkrConnected === false ? 600 : 400 }}
          >
            {ibkrConnected === null
              ? 'Checking IBKR...'
              : ibkrConnected
                ? `IBKR Connected (Port ${accountType === 'paper' ? '7497' : '7496'})`
                : `IBKR Disconnected (Port ${accountType === 'paper' ? '7497' : '7496'})`
            }
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  // This root is itself a flex item of App's outer flex box; without minWidth:0 and
  // flex:1 it sizes to its content's intrinsic width and pushes past the viewport.
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', minWidth: 0, flex: 1, width: '100%' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {visibleNavItems.find(item => item.path === location.pathname)?.text || 'Trading Assistant'}
          </Typography>
          <EvidenceBadge path={location.pathname} />
          {isLiveTrading && (
            <Chip
              icon={<WarningIcon />}
              label="LIVE TRADING"
              color="warning"
              size="small"
              sx={{ mr: 2, fontWeight: 700 }}
            />
          )}
          <Typography variant="caption" color="inherit" sx={{ opacity: 0.7, mr: 2 }}>
            {username}
          </Typography>
          <Button
            color="inherit"
            onClick={handleLogout}
            startIcon={<LogoutIcon />}
            size="small"
          >
            Logout
          </Button>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          // A flex item defaults to min-width:auto and refuses to shrink below its
          // content's intrinsic width — a page with a wide row (the exhaustion heatmap)
          // pushed the whole main area past the viewport. Let it shrink; pages own
          // their horizontal scrolling.
          minWidth: 0,
          mt: 8,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
